import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Save, RotateCcw, KeyRound, MessageCircle, Users as UsersIcon, Unlink,
  Sliders, Loader2, Shield, X, CheckCircle2, AlertTriangle, Search,
} from "lucide-react";
import { usersApi, webUsersApi, telegramApi, authApi, auth, setCurrentUser } from "../../lib/api";
import { getApiError } from "../../lib/utils";
import { TID } from "../../constants/testIds";

const DIRTY_KEYS = ["display_name", "email", "telegram_id", "telegram_username", "role"];

/**
 * Expanded detail panel for one user row. Shows BOTH web + telegram
 * surfaces side-by-side with inline editing. Persists via `usersApi.update`
 * (unified PATCH /api/users/{id}) which now covers every editable field
 * with proper uniqueness/permission gates in the backend.
 *
 * @param {object} props
 * @param {object} props.user
 * @param {object} props.taxonomy       — /hsp-permissions/taxonomy result
 * @param {object} props.me             — current signed-in user
 * @param {() => void} props.onUpdated  — refresh callback
 * @param {() => void} props.onOpenTgPerms
 * @param {() => void} props.onResetPassword
 * @param {() => void} props.onDelete
 * @param {() => void} props.onMerge
 */
export default function UserExpandedPanel({
  user, taxonomy, me,
  onUpdated, onOpenTgPerms, onResetPassword, onDelete, onMerge,
}) {
  const [form, setForm] = useState(() => initFormFromUser(user));
  const [saving, setSaving] = useState(false);
  const [unbinding, setUnbinding] = useState(false);
  const [verify, setVerify] = useState(null);   // { loading, result, error }

  useEffect(() => { setForm(initFormFromUser(user)); setVerify(null); }, [user]);

  const isSuperAdmin = me?.role === "SUPER_ADMIN";
  const isSelf = user.user_id === me?.user_id;
  const targetIsSuper = user.role === "SUPER_ADMIN";
  const canEditRole = isSuperAdmin && !targetIsSuper && !isSelf;
  const roles = taxonomy?.roles || [];

  const dirty = useMemo(() => diffDirty(user, form), [user, form]);
  const hasChanges = dirty.length > 0;

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const save = async () => {
    if (!hasChanges) return;
    const patch = buildPatch(user, form);
    if (!Object.keys(patch).length) return;
    setSaving(true);
    try {
      await usersApi.update(user.user_id, patch);
      toast.success("Saved");
      // If admin renamed themselves, refresh /me so sidebar reflects it.
      if (isSelf && patch.display_name) {
        try { setCurrentUser(await authApi.me()); } catch { /* non-fatal */ }
      }
      onUpdated?.();
    } catch (e) {
      toast.error(getApiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const revert = () => setForm(initFormFromUser(user));

  const unbindTelegram = async () => {
    if (!user.telegram_id && !user.telegram_username) return;
    if (!window.confirm(`Unbind Telegram from ${user.display_name}?`)) return;
    setUnbinding(true);
    try {
      await usersApi.unbindTelegram(user.user_id);
      toast.success("Telegram unbound");
      onUpdated?.();
    } catch (e) {
      toast.error(getApiError(e, "Unbind failed"));
    } finally {
      setUnbinding(false);
    }
  };

  const verifyBinding = async () => {
    // Prefer numeric id (stable) — fall back to username if that's all we have.
    const params = {};
    if (user.telegram_id) params.telegram_id = user.telegram_id;
    if (user.telegram_username) params.telegram_username = user.telegram_username;
    if (!params.telegram_id && !params.telegram_username) {
      toast.error("Nothing to verify — this user has no Telegram binding.");
      return;
    }
    setVerify({ loading: true });
    try {
      const result = await telegramApi.diagnose(params);
      setVerify({ loading: false, result });
    } catch (e) {
      setVerify({ loading: false, error: getApiError(e, "Verify failed") });
    }
  };

  // Both identifiers set = strongest, most robust binding.
  const hasBoth = !!user.telegram_id && !!user.telegram_username;
  const hasEither = !!(user.telegram_id || user.telegram_username);

  return (
    <div
      data-testid={TID.userExpandedPanel(user.user_id)}
      className="bg-slate-50/70 border-t border-slate-200 px-6 py-5 animate-fade-in"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* -------- Web surface -------- */}
        <section className="rounded-md border border-slate-200 bg-white p-4">
          <SectionHeader
            icon={<UsersIcon size={13} strokeWidth={2.5} />}
            label="Web account"
            hint={user.email ? "Signs in with email + password" : "No web access yet"}
          />
          <div className="mt-3 space-y-3">
            <Field label="Display name">
              <input
                data-testid={TID.editDisplayName(user.user_id)}
                value={form.display_name}
                onChange={set("display_name")}
                className={inputCls}
              />
            </Field>
            <Field label="Email" hint={!user.email && "Adding an email creates a web login. You'll see a temp password."}>
              <input
                data-testid={TID.editEmail(user.user_id)}
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="user@nationwidemh.com"
                disabled={user.email && !isSuperAdmin}
                className={inputCls + (user.email && !isSuperAdmin ? " opacity-60 cursor-not-allowed" : "")}
              />
            </Field>
            <Field label="Role">
              <select
                data-testid={TID.roleSelect(user.user_id)}
                value={form.role}
                onChange={set("role")}
                disabled={!canEditRole}
                className={inputCls + (!canEditRole ? " opacity-60 cursor-not-allowed" : "")}
              >
                {roles.map((r) => (
                  <option key={r.key} value={r.key}>{r.name || r.label || r.key.replace("_", " ")}</option>
                ))}
              </select>
              {!canEditRole && (
                <div className="mt-1 text-[10px] text-slate-500">
                  {targetIsSuper ? "Super admin role cannot be reassigned here."
                   : isSelf ? "You cannot change your own role."
                   : "Only super-admins can change roles."}
                </div>
              )}
            </Field>

            <div className="flex flex-wrap gap-2 pt-1">
              {user.email && isSuperAdmin && (
                <button
                  data-testid={TID.resetPasswordUser(user.user_id)}
                  onClick={onResetPassword}
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 hover:border-amber-500 hover:text-amber-700"
                >
                  <KeyRound size={12} /> Reset password
                </button>
              )}
              {user.must_change_password && (
                <span className="rounded-sm border border-amber-200 bg-amber-50 px-2 py-1 text-[10px] font-bold uppercase text-amber-800">
                  PW pending
                </span>
              )}
            </div>
          </div>
        </section>

        {/* -------- Telegram surface -------- */}
        <section className="rounded-md border border-slate-200 bg-white p-4">
          <SectionHeader
            icon={<MessageCircle size={13} strokeWidth={2.5} />}
            label="Telegram account"
            hint={
              hasBoth
                ? "Bound by ID + username — strongest binding"
                : hasEither
                ? "Bound — consider adding the other identifier for reliability"
                : "No Telegram binding"
            }
          />

          {/* Best-practice recommendation banner */}
          {hasEither && !hasBoth && (
            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[11px] leading-relaxed text-amber-900">
              <div className="flex items-start gap-1.5">
                <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                <div>
                  <b>Bind by BOTH numeric ID and @username</b> for the most reliable
                  bot access. The numeric ID never changes; the username lets
                  admins recognise the row at a glance.
                  {!user.telegram_id && (
                    <> Ask this user to open <b>@userinfobot</b> on Telegram — it'll reply with their exact numeric ID.</>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mt-3 space-y-3">
            <Field label="Telegram ID" hint="Numeric — get it from @userinfobot. Never changes. Leave 0 to unbind.">
              <input
                data-testid={TID.editTelegramId(user.user_id)}
                inputMode="numeric"
                value={form.telegram_id}
                onChange={(e) => set("telegram_id")(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="e.g. 5973710183"
                className={inputCls + " font-mono"}
              />
            </Field>
            <Field label="Username" hint='Human-readable. Case-insensitive. Leave blank to unbind.'>
              <input
                data-testid={TID.editTelegramUsername(user.user_id)}
                value={form.telegram_username}
                onChange={(e) => set("telegram_username")(e.target.value.replace(/^@/, ""))}
                placeholder="@handle"
                className={inputCls}
              />
            </Field>
            <Field label="Permissions">
              <div className="flex items-center gap-2">
                <button
                  data-testid={TID.tgPermsEditBtn(user.user_id)}
                  onClick={onOpenTgPerms}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 hover:border-nmi-600 hover:text-nmi-700"
                >
                  <Sliders size={12} /> Edit bot commands
                </button>
                <span className="text-[11px] text-slate-500">
                  {Array.isArray(user.telegram_permissions) && user.telegram_permissions.length
                    ? <><b className="text-slate-800">{user.telegram_permissions.length}</b> enabled</>
                    : <em>role defaults apply</em>}
                </span>
              </div>
            </Field>

            {/* Verify + Unbind row */}
            {hasEither && (
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid={TID.verifyBinding(user.user_id)}
                  onClick={verifyBinding}
                  disabled={verify?.loading}
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 hover:border-nmi-600 hover:text-nmi-700 disabled:opacity-60"
                >
                  {verify?.loading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} strokeWidth={2.5} />}
                  Verify bot binding
                </button>
                {!targetIsSuper && (
                  <button
                    data-testid={TID.unbindTgUser(user.user_id)}
                    onClick={unbindTelegram}
                    disabled={unbinding}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 hover:border-red-300 hover:text-red-700 disabled:opacity-60"
                  >
                    {unbinding ? <Loader2 size={12} className="animate-spin" /> : <Unlink size={12} />}
                    Unbind Telegram
                  </button>
                )}
              </div>
            )}

            {/* Verify result panel */}
            {verify?.result && (
              <VerifyResult data={verify.result} userId={user.user_id} onDismiss={() => setVerify(null)} />
            )}
            {verify?.error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-2.5 text-[11px] text-red-800">
                {verify.error}
              </div>
            )}
          </div>
        </section>
      </div>

      {/* -------- Meta + destructive actions -------- */}
      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-slate-200 pt-3 text-[11px] text-slate-500">
        <span>
          <b className="text-slate-700">ID</b> <code className="font-mono">{user.user_id}</code>
        </span>
        <span>
          <b className="text-slate-700">Created</b> {user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
        </span>
        <span>
          <b className="text-slate-700">Last sign-in</b> {user.last_login_at ? new Date(user.last_login_at).toLocaleString() : <em>never</em>}
        </span>
        {user.is_owner && (
          <span className="inline-flex items-center gap-1 rounded-sm border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-800">
            <Shield size={10} /> Owner
          </span>
        )}
      </div>

      {/* -------- Bottom action bar -------- */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          {isSuperAdmin && !targetIsSuper && !isSelf && (
            <button
              data-testid={TID.mergeUser(user.user_id)}
              onClick={onMerge}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 hover:border-nmi-600 hover:text-nmi-700"
            >
              Merge into another user…
            </button>
          )}
          {!targetIsSuper && !isSelf && !user.is_owner && (
            <button
              data-testid={TID.deleteUser(user.user_id)}
              onClick={onDelete}
              className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-red-700 hover:bg-red-50"
            >
              Delete user
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={revert}
            disabled={!hasChanges || saving}
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
          >
            <RotateCcw size={12} /> Revert
          </button>
          <button
            data-testid={TID.saveUser(user.user_id)}
            onClick={save}
            disabled={!hasChanges || saving}
            className="inline-flex items-center gap-1 rounded-md bg-nmi-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-nmi-800 disabled:opacity-40"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Save {hasChanges ? <span className="rounded bg-white/20 px-1 text-[10px]">{dirty.length}</span> : ""}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- helpers ----------
function initFormFromUser(u) {
  return {
    display_name: u.display_name || "",
    email: u.email || "",
    role: u.role || "VIEWER",
    telegram_id: u.telegram_id ? String(u.telegram_id) : "",
    telegram_username: u.telegram_username || "",
  };
}

function diffDirty(u, f) {
  const dirty = [];
  if ((f.display_name || "").trim() !== (u.display_name || "")) dirty.push("display_name");
  if ((f.email || "").toLowerCase().trim() !== (u.email || "").toLowerCase()) dirty.push("email");
  if (f.role !== (u.role || "VIEWER")) dirty.push("role");
  const tid = f.telegram_id === "" ? null : parseInt(f.telegram_id, 10);
  if (tid !== (u.telegram_id ?? null)) dirty.push("telegram_id");
  if ((f.telegram_username || "").toLowerCase() !== (u.telegram_username || "").toLowerCase()) dirty.push("telegram_username");
  return dirty.filter((k) => DIRTY_KEYS.includes(k));
}

function buildPatch(u, f) {
  const patch = {};
  if ((f.display_name || "").trim() !== (u.display_name || "")) {
    patch.display_name = f.display_name.trim();
  }
  if ((f.email || "").toLowerCase().trim() !== (u.email || "").toLowerCase()) {
    if (f.email.trim()) patch.email = f.email.trim();
  }
  if (f.role !== (u.role || "VIEWER")) patch.role = f.role;
  const tidStr = (f.telegram_id || "").trim();
  const tid = tidStr === "" ? 0 : parseInt(tidStr, 10);
  if (tid !== (u.telegram_id ?? 0)) {
    patch.telegram_id = Number.isNaN(tid) ? 0 : tid;
  }
  const uname = (f.telegram_username || "").trim();
  if (uname.toLowerCase() !== (u.telegram_username || "").toLowerCase()) {
    patch.telegram_username = uname; // "" is explicit unbind
  }
  return patch;
}

// ---------- tiny sub-components ----------
const inputCls =
  "w-full rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-nmi-600 focus:ring-2 focus:ring-nmi-600/20";

const Field = ({ label, hint, children }) => (
  <div>
    <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
      {label}
    </label>
    {children}
    {hint && <div className="mt-1 text-[10px] text-slate-500">{hint}</div>}
  </div>
);

const SectionHeader = ({ icon, label, hint }) => (
  <div>
    <div className="flex items-center gap-1.5">
      <span className="text-nmi-700">{icon}</span>
      <h4 className="font-heading text-sm font-bold text-slate-900">{label}</h4>
    </div>
    {hint && <p className="mt-0.5 text-[11px] text-slate-500">{hint}</p>}
  </div>
);

// -------- Verify-binding result card --------
// Shows the server's verdict for this user's Telegram identifiers.
// ACCEPT → green: the bot WILL respond to this person.
// REJECT_UNBOUND → red: bot silently rejects; explains why + how to fix.
const VerifyResult = ({ data, userId, onDismiss }) => {
  const accepted = data.verdict === "ACCEPT";
  return (
    <div
      data-testid={TID.verifyBindingResult(userId)}
      className={`rounded-md border p-3 text-[11px] leading-relaxed ${
        accepted
          ? "border-emerald-200 bg-emerald-50 text-emerald-900"
          : "border-red-200 bg-red-50 text-red-900"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-1.5">
          {accepted
            ? <CheckCircle2 size={13} className="mt-0.5 flex-shrink-0" strokeWidth={2.5} />
            : <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" strokeWidth={2.5} />}
          <div className="min-w-0">
            <div className="font-bold uppercase tracking-wider text-[10px]">
              {accepted ? "Bot verdict — accept" : "Bot verdict — reject"}
            </div>
            <div className="mt-0.5">{data.reason}</div>
            {data.matches && data.matches.length > 1 && (
              <div className="mt-1 text-[10px]">
                ⚠ Multiple rows match ({data.matches.length}). Use Users → Merge to collapse them.
              </div>
            )}
            {!accepted && (
              <div className="mt-1 text-[10px] opacity-80">{data.note}</div>
            )}
          </div>
        </div>
        <button
          onClick={onDismiss}
          aria-label="Dismiss verify result"
          className={`shrink-0 rounded p-0.5 ${accepted ? "hover:bg-emerald-100" : "hover:bg-red-100"}`}
        >
          <X size={12} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
};
